import { Store } from 'hooks/createStore';
import { CustomCreatable } from 'presentation/commonTypes';
import { DirectoryModel } from 'shared/domain/directory/directoryModel';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { ImageConvertStatus } from 'shared/domain/imageConvertStatus/imageConvertStatus';
import { DocumentationBaseIds } from 'shared/types/documentation';
import {
  FILE_STATE_KIND,
  FileType,
  isSavedFileType,
  isUnsavedFileType,
} from 'components/common/graphicUploader/types';

type DocumentationFileBase = FileType &
  DocumentationBaseIds &
  CustomCreatable & {
    localId: number;
    imageConvertStatus: ImageConvertStatus | undefined;
  };

export type DocumentationLocalFile = DocumentationFileBase & {
  fileStateKind: typeof FILE_STATE_KIND.UNSAVED;
};

export type DocumentationSavedFile = DocumentationFileBase & {
  fileStateKind: typeof FILE_STATE_KIND.SAVED;
  documentationId: string;
  versionId: string;
};

export type DocumentationFile =
  | DocumentationLocalFile
  | DocumentationSavedFile;

export function isSavedDocumentationFile(
  file: DocumentationFile
): file is DocumentationSavedFile {
  return (
    isSavedFileType(file) &&
    Boolean(file.documentationId) &&
    Boolean(file.versionId)
  );
}

export enum DocumentationViewItemType {
  DIRECTORY = 'DIRECTORY',
  FILE = 'FILE',
}

interface DocumentationViewItemBase {
  uniqueKeyId: string;
  type: DocumentationViewItemType;
}

interface DocumentationViewItemMap {
  [DocumentationViewItemType.DIRECTORY]: DirectoryModel;
  [DocumentationViewItemType.FILE]: DocumentationModel;
}

export type DocumentationViewItem = {
  [K in DocumentationViewItemType]: DocumentationViewItemMap[K] &
    DocumentationViewItemBase;
}[DocumentationViewItemType];

export function isDirectoryItemType(
  type: DocumentationViewItemType | undefined
): type is DocumentationViewItemType.DIRECTORY {
  return type === DocumentationViewItemType.DIRECTORY;
}

export function isFileItemType(
  type: DocumentationViewItemType
): type is DocumentationViewItemType.FILE {
  return type === DocumentationViewItemType.FILE;
}

export type DocumentationFileViewItem = DocumentationModel & DocumentationViewItemBase;

export function isFileItem(
  item: DocumentationViewItem
): item is DocumentationFileViewItem {
  return item.type === DocumentationViewItemType.FILE;
}

export interface DocumentationUploader {
  addDocumentationsFromFiles: (
    files: File[],
    targetDirectoryId: string | null
  ) => void;
}

export interface DirectoryUploader {
  addDirectoriesFromFiles: (
    files: File[],
    targetDirectoryId: string | null
  ) => void;
  assignFilesToDirectories: (
    files: File[],
    directoriesMap: Record<string, string>
  ) => void;
}
